import * as React from 'react';
import * as _ from 'lodash';
import {translate} from 'react-i18next';
// reducer
import {ITranslatedComponent} from "../redux/reducers/generalReducer";
import {WorkflowStatusDtoType} from "../services/Client";

/**
 * IStateProperty represents the properties each state view has.
 *  Each state can have different color and hover text.
 */
export interface IStateProperty {
    /** The id or index of the state. */
    id: number;
    /** The state permission. */
    role: string;
    /** The id of the role. */
    roleId: number;
    /** Color of the state in hexadecimal. */
    color?: string;
    /** The text displayed when the mouse is hovering on the state. */
    hoverText?: string;
    /** The reason of the rejection. */
    rejectReason: string;
    /** Indicates if object has been rejected
     * @default false
     */
    isRejected?: boolean;
    /** Type of the state. */
    type: WorkflowStatusDtoType;
    /** Indicates if the step is a reset point */
    isResetPoint: boolean;
}

/**
 * onChange handler for request of changing of states
 * Whether the state will be changed is the decision of the handler function,
 * the state will not be changed internally in the StateViewBar!
 * @param {number} stateId  new state requested to be set
 */
type OnChangeStateViewBarHandler = (stateId: number) => void;
interface IStateViewBarProps extends ITranslatedComponent {
    /** Maximum height of the whole component. */
    height: number;
    /** Array of state button porperties */
    states: IStateProperty[];
    /** Currently active state (id) */
    active: number;
    /** style of SVG element */
    style?: HTMLStyleElement;

    /** activates edit mode - in this mode the onChange events will be generated
     * @default false
     */
    editMode?: boolean;

    /**
     * defines the type of changing states.
     * By true the next state will be send in onChange event.
     * By false the clicked state will be send in onChange event.
     * @default false
     */
    changeTypeSequential?: boolean;

    /**
     * if editMode is true, this event will be generated if the user clicks on some state icon.
     * Whether the state will be changed is the decision of the handler function,
     * the state will not be changed internally in the StateViewBar!
     */
    onChange?: OnChangeStateViewBarHandler;
}

const pos = (index: number): number => {
    return 25 + 100 * index;
};

/**
 * send onChange event with next required state
 * @param props properties of state view bar
 * @param nextId if defined, the next required state, if not, the next state will be generated as the
 *                next state to props.active.
 */
const callOnChange = (props: IStateViewBarProps, nextId?: number) => {
    if (props.editMode && props.onChange) {
        if (nextId !== undefined) // because of 0 use undefined!!!
        {
            props.onChange(nextId);
        }
        else {
            const iActive: number = _.findIndex(props.states, (value: IStateProperty) => value.id === props.active);
            if (iActive >= 0) {
                const next = props.states[(iActive + 1) % props.states.length];
                props.onChange(next.id);
            }
        }
    }
};

/*
  Component that show an array of states and in which it can be defined a current state.

  For each state it can be defined a color and a hover text.

  The whole component uses as less space vertically as possible and it can be defined a
  maximumHeight for the component to use. The width of the component is calculated relative
  to the height.
*/
const StateViewBar: React.SFC<IStateViewBarProps> = (props: IStateViewBarProps) => {
    // Calculates the width of the component based on the height of the component
    // and the number of states the component has.
    var width = (((props.states.length - 1) * 100) + 50);
    var calcWidth = (props.height * width) / 50;

    // css value for the dimensions of the whole container viewBox
    var vp = "0 0 " + width + " 50";

    var r1 = 11;
    var r2 = 18;

    return (
        <div style={{width: calcWidth}}>
            <svg
                viewBox={vp}
                style={{height: props.height}}
                onClick={
                    () => {
                        if (props.changeTypeSequential) {
                            callOnChange(props);
                        }
                    }
                }
            >
                <line x1={pos(0)} y1={25} x2={pos(props.states.length - 1)} y2={25} stroke="#000000" strokeWidth={5} />
                {props.states.map(function (item: IStateProperty, index: number) {
                    if (item.isRejected === true) {
                        const points = [
                            [pos(index) - r2, 25 + r2].join(','),
                            [pos(index) + r2, 25 + r2].join(','),
                            [pos(index), 25 - r2].join(',')
                        ].join(' ');
                        const fillColor = props.active === item.id ? item.color : "#ff0000";

                        return (<polygon
                            key={index}
                            points={points}
                            fill={fillColor}
                            stroke="#ff0000"
                            onClick={
                                () => {
                                    if (!props.changeTypeSequential) {
                                        callOnChange(props, item.id);
                                    }
                                }
                            }
                        >
                            <title>{item.hoverText + " (" + props.t("REJECTED") + ")"}</title>
                        </polygon>);
                    } else if (props.active !== item.id) {
                        return (
                            <rect
                                key={index}
                                x={pos(index) - r1}
                                y={25 - r1}
                                width={r1 * 2}
                                height={r1 * 2}
                                fill="#7f7f7f"
                                stroke="#000000"
                                onClick={
                                    () => {
                                        if (!props.changeTypeSequential) {
                                            callOnChange(props, item.id);
                                        }
                                    }
                                }
                            >
                                <title>{item.hoverText}</title>
                            </rect>);
                    } else {
                        return (<circle key={index} cx={pos(index)} cy={25} r={r2} fill={item.color} stroke="#000000" strokeWidth={3}>
                            <title>{item.hoverText}</title>
                        </circle>);
                    }
                })}
            </svg>
        </div>
    );
};

export default translate("GLOBAL")(StateViewBar);



// WEBPACK FOOTER //
// ./src/components/StateViewBar.tsx